import axios from 'axios';
// Make sure axios is available for direct usage

// Define API URL with fallback
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// Define API key with fallback
const API_KEY = process.env.REACT_APP_API_KEY || 'XVCjNbpnolBvhOJMyRKImAeqyNyLwzFs';

// Log the API URL for debugging
console.log('API URL:', API_URL);
console.log('Using API Key:', API_KEY.substring(0, 5) + '...');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY
  }
});

// Add token to requests if exists
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      // Log authorization header for debugging
      console.log(`Setting Authorization header: Bearer ${token.substring(0, 20)}...`);
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Log the full request for debugging
    console.log(`API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`, 
                'Headers:', config.headers);
                
    return config;
  },
  error => Promise.reject(error)
);

// Define Search API functions
export const searchApi = {
  /**
   * Perform global search across multiple entity types based on user role
   * @param {string} query - Search query string
   * @param {Object} params - Optional parameters
   * @param {number} params.limit_per_type - Maximum results per entity type
   * @returns {Promise} API response with search results
   */
  globalSearch: (query, params = {}) => {
    const { limit_per_type = 5 } = params;
    return api.get(`/search/global?query=${encodeURIComponent(query)}&limit_per_type=${limit_per_type}`);
  },

  /**
   * Search for products
   * @param {string} query - Search query string
   * @param {Object} params - Optional parameters
   * @param {number} params.limit - Maximum results
   * @returns {Promise} API response with product search results
   */
  searchProducts: (query, params = {}) => {
    const { limit = 10 } = params;
    return api.get(`/search/products?query=${encodeURIComponent(query)}&limit=${limit}`);
  }
};

// Define RM API functions
export const rmApi = {
  /**
   * Get a list of relationship managers with optional parameters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term
   * @param {string} params.status - Optional status filter (Active/Inactive/All)
   * @returns {Promise} API response
   */
  getRelationshipManagers: (params = {}) => {
    const { skip = 0, limit = 10, search, status } = params;
    let url = `/admin/relationship-managers?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get a specific relationship manager by ID
   * @param {number} rmId - RM ID
   * @returns {Promise} API response
   */
  getRelationshipManagerById: (rmId) => {
    return api.get(`/admin/relationship-manager/${rmId}`);
  },
  
  /**
   * Create a new relationship manager
   * @param {Object} rmData - RM data
   * @returns {Promise} API response
   */
  createRelationshipManager: (rmData) => {
    return api.post('/admin/relationship-manager', rmData);
  },
  
  /**
   * Toggle RM status (active/inactive)
   * @param {number} rmId - RM ID
   * @param {boolean} active - New status
   * @returns {Promise} API response
   */
  toggleRmStatus: (rmId, active) => {
    return api.put(`/admin/relationship-manager/${rmId}/status?active=${active}`);
  },
  
  /**
   * Update an existing relationship manager
   * @param {number} rmId - RM ID
   * @param {Object} rmData - RM data to update
   * @returns {Promise} API response
   */
  updateRelationshipManager: (rmId, rmData) => {
    return api.put(`/admin/relationship-manager/${rmId}`, rmData);
  },

  /**
   * Get details for the currently logged-in RM
   * @returns {Promise} API response with RM details (matching RMResponse schema)
   */
  getRmDetails: () => {
    // Calls the new backend endpoint GET /api/rm/details
    return api.get('/rm/details');
  }, // Add comma here if missing

  // --- Add this function ---
  /**
   * Get a list of DSOs assigned to the current RM
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term
   * @param {string} params.status_filter - Optional status filter
   * @returns {Promise} API response with DSO list
   */
  getAssignedDsos: (params = {}) => {
    const { skip = 0, limit = 10, search, status_filter } = params; // Use status_filter to match backend
    // Ensure this endpoint matches the one defined in backend/app/routers/rm.py
    let url = `/rm/dsos?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status_filter && status_filter !== 'All') { // Check if filter is applied and not 'All'
      url += `&status_filter=${encodeURIComponent(status_filter)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get detailed information for a specific DSO assigned to the current RM
   * @param {number} dsoId - DSO user ID
   * @returns {Promise} API response with detailed DSO information
   */
  getDsoDetails: (dsoId) => {
    return api.get(`/rm/dsos/${dsoId}`);
  },

  // --- Add this function ---
  /**
   * Get agents associated with a specific DSO under the current RM
   * @param {number} dsoId - The User ID of the DSO
   * @param {Object} params - Query parameters (e.g., skip, limit, search)
   * @returns {Promise} API response with agent list
   */
  getDsoAgents: (dsoId, params = {}) => {
    const { skip = 0, limit = 10, search } = params;
    let url = `/rm/dsos/${dsoId}/agents?skip=${skip}&limit=${limit}`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    // Add status_filter param if needed later

    return api.get(url);
  },
  // --- End of added function ---

  /**
   * Get current commission settings for a specific DSO.
   * @param {number} dsoId - The User ID of the DSO.
   * @returns {Promise} API response, expected to contain commission data (e.g., [{ product_id: X, commission_percentage: Y }, ...])
   */
  getDsoCommissions: (dsoId) => {
    // Define the endpoint based on your backend router structure for RM getting DSO commissions
    // Example: /api/rm/dsos/{dso_id}/commissions
    return api.get(`/rm/dsos/${dsoId}/commissions`);
  },

  /**
   * Set or update commission settings for a specific DSO.
   * @param {number} dsoId - The User ID of the DSO.
   * @param {Array} commissionsData - Array of objects like { product_id: number, commission_percentage: number }
   * @returns {Promise} API response
   */
  setDsoCommissions: (dsoId, commissionsData) => {
    // Since the backend has issues with the 'is_active' field, we need to create a
    // custom implementation that uses direct SQL or a different approach
    
    // For now, let's implement a sequential approach
    return new Promise(async (resolve, reject) => {
      try {
        // Get all existing commissions first
        const existingCommissionsResponse = await api.get(`/rm/dsos/${dsoId}/commissions`);
        const existingCommissions = existingCommissionsResponse.data.items || 
                                   existingCommissionsResponse.data || [];
        
        const results = [];
        
        // Process each commission one by one
        for (const commission of commissionsData) {
          try {
            // Find if this product already has a commission
            const existingComm = existingCommissions.find(
              (item) => item.product_id === commission.product_id
            );
            
            let result;
            
            if (existingComm) {
              // Update existing commission
              result = await api.patch(
                `/rm/dsos/${dsoId}/commissions/${existingComm.id}`,
                { commission_percentage: commission.commission_percentage, commission_amount: commission.commission_amount }
              );
            } else {
              // Create new commission using the direct endpoint
              // We're using axios directly to avoid any possible middleware issues
              
              // Make a pure axios request to bypass any framework processing
              const token = localStorage.getItem('token');
              const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'x-api-key': API_KEY
              };
              
              result = await axios({
                method: 'post',
                url: `${API_URL}/rm/dsos/${dsoId}/commissions`,
                headers: headers,
                data: {
                  product_id: commission.product_id,
                  commission_percentage: commission.commission_percentage,
                  commission_amount: commission.commission_amount
                }
              });
            }
            
            results.push(result);
          } catch (error) {
            console.error(`Error processing commission for product ${commission.product_id}:`, error);
            results.push({ error: error.message, product_id: commission.product_id });
          }
        }
        
        resolve(results);
      } catch (error) {
        console.error("Error in commission processing:", error);
        reject(error);
      }
    });
  },

  getLeads: (params = {}) => {
    const { skip = 0, limit = 10, dso_id, status, search } = params;
    let url = `/rm/leads?skip=${skip}&limit=${limit}`;
    
    if (dso_id !== undefined && dso_id !== null) {
      url += `&dso_id=${parseInt(dso_id)}`;
    }
    
    if (status !== undefined) {
      url += `&status=${status}`;
    }
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    console.log('Fetching leads with URL:', url);
    return api.get(url);
  },

  /**
   * Get invoices generated by DSOs assigned to the current RM
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {number} params.dso_id - Optional filter by specific DSO
   * @param {string} params.invoice_number - Optional filter by invoice number
   * @param {string} params.approval_status - Optional filter by approval status ("Pending", "Approved", "Rejected")
   * @param {string} params.payment_status - Optional filter by payment status ("Generated", "Completed", "Failed")
   * @returns {Promise} API response with invoice list
   */
  getInvoices: (params = {}) => {
    const { skip = 0, limit = 10, dso_id, invoice_number, approval_status, payment_status } = params;
    // Ensure this endpoint matches the one defined in backend/app/routers/rm.py
    let url = `/rm/invoices?skip=${skip}&limit=${limit}`;
    
    // Add filter params if provided
    if (dso_id) {
      url += `&dso_id=${encodeURIComponent(dso_id)}`;
    }
    
    if (invoice_number) {
      url += `&invoice_number=${encodeURIComponent(invoice_number)}`;
    }
    
    if (approval_status) {
      url += `&approval_status=${encodeURIComponent(approval_status)}`;
    }
    
    if (payment_status) {
      url += `&payment_status=${encodeURIComponent(payment_status)}`;
    }
    
    return api.get(url);
  },
  // --- End of added function ---

  /**
   * Update the status of a specific invoice (Approve/Reject)
   * @param {number} invoiceId - The ID of the invoice
   * @param {string} status - The new status ("Approved" or "Rejected")
   * @returns {Promise} API response
   */
  updateInvoiceStatus: (invoiceId, status) => {
    // Makes PUT request to update invoice status
    return api.put(`/rm/invoices/${invoiceId}/status`, { status });
  },
  
  /**
   * Update the payment status of a specific invoice
   * @param {number} invoiceId - The ID of the invoice
   * @param {number} paymentStatus - The new payment status (1=Payment Completed, 2=Payment Failed)
   * @returns {Promise} API response
   */
  updateInvoicePaymentStatus: (invoiceId, paymentStatus) => {
    // Makes PUT request to update invoice payment status
    return api.put(`/rm/invoices/${invoiceId}/payment-status?payment_status=${paymentStatus}`);
  },
  
  /**
   * Create a new payout as an RM for a DSO
   * @param {Object} payoutData - Payout data
   * @param {number} payoutData.dso_id - The DSO user ID
   * @param {Array<number>} payoutData.case_ids - Array of case IDs to include in payout
   * @returns {Promise} API response with created payout details
   */
  createPayout: (payoutData) => {
    return api.post('/rm/payouts/create', payoutData);
  },

  // --- Add this function ---
  /**
   * Get payouts generated by DSOs assigned to the current RM
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {number} params.dso_id - Optional DSO filter
   * @param {number} params.product_id - Optional product filter  
   * @param {number} params.status - Optional status filter (0: Requested, 1: Approved, 2: Disbursed)
   * @returns {Promise} API response with payout list
   */
  getPayouts: (params = {}) => {
    const { skip = 0, limit = 10, dso_id, product_id, status } = params;
    // Ensure this endpoint matches the one defined in backend/app/routers/rm.py
    let url = `/rm/payouts?skip=${skip}&limit=${limit}`;
    
    // Add filter params if provided
    if (dso_id) {
      url += `&dso_id=${encodeURIComponent(dso_id)}`;
    }
    
    if (product_id) {
      url += `&product_id=${encodeURIComponent(product_id)}`;
    }
    
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get details of a specific payout
   * @param {number} payoutId - The ID of the payout to retrieve
   * @returns {Promise} API response with detailed payout information
   */
  getPayoutDetails: (payoutId) => {
    return api.get(`/rm/payouts/${payoutId}`);
  },
  
  /**
   * Get cases associated with a specific payout
   * @param {number} payoutId - The ID of the payout
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with payout cases list
   */
  getPayoutCases: (payoutId, params = {}) => {
    const { skip = 0, limit = 10 } = params;
    return api.get(`/rm/payouts/${payoutId}/cases?skip=${skip}&limit=${limit}`);
  },
  
  /**
   * Get invoices associated with a specific payout
   * @param {number} payoutId - The ID of the payout
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with payout invoices list
   */
  getPayoutInvoices: (payoutId, params = {}) => {
    const { skip = 0, limit = 10 } = params;
    return api.get(`/rm/payouts/${payoutId}/invoices?skip=${skip}&limit=${limit}`);
  },
  
  /**
   * Get all cases associated with a specific DSO
   * @param {number} dsoId - The user ID of the DSO
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term for lead name or application number
   * @param {string} params.status_filter - Optional filter by case status (1=Requested, 2=Approved, 3=Disbursed)
   * @returns {Promise} API response with DSO cases list
   */
  getDsoCases: (dsoId, params = {}) => {
    const { skip = 0, limit = 10, search, status_filter } = params;
    let url = `/rm/dsos/${dsoId}/cases?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status_filter) {
      url += `&status_filter=${encodeURIComponent(status_filter)}`;
    }
    
    return api.get(url);
  },
  // --- End of added functions ---


  // --- Add this function ---
  /**
   * Get dashboard statistics for the current RM
   * @returns {Promise} API response with dashboard stats
   */
  getDashboardStats: () => {
    // Ensure this endpoint matches the one defined in backend/app/routers/rm.py
    return api.get('/rm/dashboard-stats');
  },
  // --- End of added function ---


  // --- Add this function ---
  /**
   * Get upcoming (requested) payouts for the current RM
   * @param {number} limit - Maximum number of payouts to fetch (defaults to 5)
   * @returns {Promise} API response with upcoming payout list
   */
  getUpcomingPayouts: (limit = 5) => {
    // Ensure this endpoint matches the one defined in backend/app/routers/rm.py
    return api.get(`/rm/upcoming-payouts?limit=${limit}`);
  },
  // --- End of added function ---

  /**
   * Get RM's agent commissions for a specific period
   * @param {Object} params - Query parameters (period, month, year)
   * @returns {Promise} API response
   */
  getAgentCommissionsData: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.month) queryParams.append('month', params.month);
    if (params.year) queryParams.append('year', params.year);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return api.get(`/rm/agent-commissions${queryString}`);
  },
  
  /**
   * Update RM's agent commission settings
   * @param {Object} commissionData - Commission update data
   * @returns {Promise} API response
   */
  updateAgentCommissionSettings: (commissionData) => {
    return api.put('/rm/agent-commissions', commissionData);
  },
}; // Ensure this closing brace exists for rmApi

// --- Add this new productApi object ---
export const productApi = {
  /**
   * Get a list of all products
   * @param {Object} params - Query parameters (e.g., skip, limit, dso_id) // Add dso_id here
   * @returns {Promise} API response with product list { items: [], total: number }
   */
  getProducts: (params = {}) => {
    // Use a high limit initially to fetch all products for grouping
    // Pagination can be added later if needed
    const { skip = 0, limit = 500, dso_user_id, category_id } = params; // Added category_id
    
    // Build query string
    let queryString = `?skip=${skip}&limit=${limit}`;
    if (dso_user_id !== undefined && dso_user_id !== null) {
      queryString += `&dso_user_id=${dso_user_id}`;
    }
    
    // Add category_id parameter if provided
    if (category_id !== undefined && category_id !== null) {
      queryString += `&category_id=${category_id}`;
    }
    
    // Ensure this endpoint matches your backend route for listing products
    // The base path is already handled by the axios instance, so just use /products
    return api.get(`/products${queryString}`); // Use the constructed query string
  },

  /**
   * Get a specific product by its ID
   * @param {number | string} productId - The ID of the product to fetch
   * @returns {Promise} API response with product details
   */
  getProductById: (productId) => {
    return api.get(`/products/${productId}`);
  },

  /**
   * Create a new product (Admin only)
   * @param {Object} productData - Data for the new product (matching ProductCreate schema)
   * @returns {Promise} API response with the created product details
   */
  createProduct: (productData) => {
    return api.post('/products', productData);
  },

  /**
   * Update an existing product (Admin only)
   * @param {number | string} productId - The ID of the product to update
   * @param {Object} productData - Data to update (matching ProductUpdate schema)
   * @returns {Promise} API response with the updated product details
   */
  updateProduct: (productId, productData) => {
    return api.put(`/products/${productId}`, productData);
  },

  /**
   * Delete a product (Admin only)
   * @param {number | string} productId - The ID of the product to delete
   * @returns {Promise} API response (status 204 expected on success)
   */
  deleteProduct: (productId) => {
    return api.delete(`/products/${productId}`);
  },

  /**
   * Get a list of all available banks
   * @returns {Promise} API response with bank list { items: [], total: number }
   */
  getBanks: (params = {}) => {
    const { skip = 0, limit = 1000 } = params; // Fetch a large number by default
    // Uses the public /banks endpoint for product dropdowns
    return api.get(`/banks?skip=${skip}&limit=${limit}`);
  },

  /**
   * Get a list of all available categories
   * @returns {Promise} API response with category list { items: [], total: number }
   */
  getCategories: (params = {}) => {
    const { skip = 0, limit = 1000 } = params; // Fetch a large number by default
    // Assumes a /categories endpoint exists in the backend
    return api.get(`/categories?skip=${skip}&limit=${limit}`);
  },
};

// Define DSO API functions
// Define Admin API functions
export const adminApi = {
  /**
   * Get global commissions for a specific DSO
   * @param {number} dsoId - The DSO ID
   * @returns {Promise} API response with commission data
   */
  getDsoCommissions: (dsoId) => {
    return api.get(`/admin/commissions/dsos/${dsoId}`);
  },

  /**
   * Update global commissions for a specific DSO
   * @param {number} dsoId - The DSO ID
   * @param {Array} commissionData - Array of commission objects
   * @returns {Promise} API response
   */
  updateDsoCommissions: (dsoId, commissionData) => {
    return api.post(`/admin/commissions/dsos/${dsoId}`, commissionData);
  },
  
  /**
   * Get a list of banks with optional parameters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term
   * @returns {Promise} API response with list of banks
   */
  getBanks: (params = {}) => {
    const { skip = 0, limit = 10, search = '' } = params;
    let url = `/admin/banks?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get a specific bank by ID
   * @param {number} bankId - The bank ID
   * @returns {Promise} API response with bank details
   */
  getBankById: (bankId) => {
    return api.get(`/admin/banks/${bankId}`);
  },
  
  /**
   * Create a new bank
   * @param {Object} bankData - Bank data
   * @returns {Promise} API response
   */
  createBank: (bankData) => {
    return api.post('/admin/banks', bankData);
  },
  
  /**
   * Update an existing bank
   * @param {number} bankId - The bank ID
   * @param {Object} bankData - Bank data to update
   * @returns {Promise} API response
   */
  updateBank: (bankId, bankData) => {
    return api.put(`/admin/banks/${bankId}`, bankData);
  },
  
  /**
   * Delete a bank
   * @param {number} bankId - The bank ID to delete
   * @returns {Promise} API response
   */
  deleteBank: (bankId) => {
    return api.delete(`/admin/banks/${bankId}`);
  }
};

export const dsoApi = {
  /**
   * Create a new DSO
   * @param {Object} dsoData - DSO data matching backend DSOCreate schema
   * @returns {Promise} API response
   */
  createDso: (dsoData) => {
    return api.post('/admin/dso', dsoData);
  },
  
  /**
   * Verify PAN card details using Karza API
   * @param {Object} panData - PAN verification data
   * @param {string} panData.name - Full name as in PAN card
   * @param {string} panData.dob - Date of birth in DD-MM-YYYY format
   * @param {string} panData.pan - PAN card number
   * @param {number} panData.lead_id - Lead ID for reference
   * @returns {Promise} API response with verification result
   */
  verifyPanCard: (panData) => {
    return api.post('/karza/verify-pan', panData);
  },
  
  /**
   * Get team performance data for charts
   * @param {string} period - Period to fetch data for (daily, weekly, monthly, lastMonth)
   * @returns {Promise} API response with labels, leads and conversions data
   */
  getTeamPerformance: (period = 'daily') => {
    return api.get(`/dso/dashboard/performance?period=${period}`);
  },
  
  /**
   * Get top performing agents data
   * @param {string} period - Period to fetch data for (daily, weekly, monthly, lastMonth)
   * @param {number} limit - Maximum number of agents to return
   * @returns {Promise} API response with list of top agents
   */
  getTopAgents: (period = 'daily', limit = 5) => {
    return api.get(`/dso/dashboard/top-agents?period=${period}&limit=${limit}`);
  },

  /**
   * Get a list of DSOs with optional parameters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term
   * @param {string} params.status - Optional status filter
   * @param {number} params.rm_id - Optional relationship manager ID filter
   * @returns {Promise} API response
   */
  getDsoList: (params = {}) => {
    const { skip = 0, limit = 10, search, status, rm_id } = params;
    let url = `/admin/dsos?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }

    if (rm_id) {
      url += `&rm_id=${rm_id}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get a specific DSO by ID
   * @param {number} id - The DSO ID to fetch
   * @returns {Promise} API response with DSO data
   */
  getDsoById: (id) => {
    return api.get(`/admin/dso/${id}`);
  },
  
  /**
   * Update an existing DSO
   * @param {number} id - The DSO ID to update
   * @param {Object} dsoData - DSO data to update
   * @returns {Promise} API response
   */
  updateDso: (id, dsoData) => {
    return api.put(`/admin/dso/${id}`, dsoData);
  },
  
  /**
   * Reset DSO's password
   * @param {number} id - The DSO ID
   * @param {string} newPassword - The new password to set
   * @returns {Promise} API response
   */
  resetDsoPassword: (id, newPassword) => {
    return api.put(`/admin/dso/${id}/reset-password`, { password: newPassword });
  },
  
  /**
   * Deactivate a DSO
   * @param {number} id - The DSO ID to deactivate
   * @returns {Promise} API response
   */
  toggleDsoStatus: (id, active) => {
    return api.put(`/admin/dso/${id}/status?active=${active}`);
  }, // Add comma here

  /**
   * Bulk upload agents for a specific DSO
   * @param {number} dsoId - The ID of the DSO
   * @param {FormData} formData - FormData object containing the CSV file under the key 'file'
   * @returns {Promise} API response
   */
  bulkUploadAgents: (dsoId, formData) => {
    // Assuming the backend endpoint is /admin/dso/{dso_id}/bulk-upload-agents
    return api.post(`/admin/dso/${dsoId}/bulk-upload-agents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Important for file uploads
      },
      // Optional: Add progress tracking if needed
      // onUploadProgress: progressEvent => {
      //   console.log('Upload Progress: ' + Math.round((progressEvent.loaded * 100) / progressEvent.total));
      // }
    });
  }, // Add comma here

  /**
   * Get agents associated with a specific DSO
   * @param {number} dsoId - The ID of the DSO
   * @param {Object} params - Query parameters (e.g., skip, limit)
   * @returns {Promise} API response with agent list
   */
  getAgentsForDso: (dsoId, params = {}) => {
    const { skip = 0, limit = 1000 } = params; // Default high limit
    // Ensure the endpoint matches the backend router definition
    return api.get(`/admin/dso/${dsoId}/agents?skip=${skip}&limit=${limit}`);
  },

  /**
   * Get the profile of the currently authenticated DSO
   * @returns {Promise} API response with the DSO profile details
   */
  getDsoProfile: () => {
    return api.get('/dso/profile');
  },

  /**
   * Update the profile of the currently authenticated DSO
   * @param {Object} profileData - The profile data to update
   * @returns {Promise} API response with the updated DSO profile
   */
  updateDsoProfile: (profileData) => {
    // Check if profileData is FormData (for file uploads)
    if (profileData instanceof FormData) {
      return api.put('/dso/profile', profileData, {
        headers: {
          'Content-Type': undefined // Let Axios set the correct Content-Type with boundary
        }
      });
    }
    return api.put('/dso/profile', profileData);
  },

  /**
   * Upload a document for the currently authenticated DSO
   * @param {FormData} formData - FormData with file and document_type
   * @returns {Promise} API response with the uploaded file URL and updated profile
   */
  uploadDocument: (formData) => {
    return api.post('/dso/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  /**
   * Upload a DSO document - specific method for DSO onboarding
   * @param {FormData} formData - FormData with file and document_type
   * @returns {Promise} API response with the uploaded file URL and updated profile
   */
  uploadDsoDocument: (formData) => {
    // Extra logging for debugging
    const docType = formData.get('document_type');
    const file = formData.get('file');
    
    
    return api.post('/dso/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }});
  },
  
  /**
   * Update bank details for the currently authenticated DSO
   * @param {Object} bankDetails - Bank details data
   * @returns {Promise} API response with updated bank details
   */
  updateDsoBankDetails: (bankDetails) => {
    return api.put('/dso/bank-details', bankDetails);
  },

  /**
   * Sign the DSO agreement
   * @returns {Promise} API response with success message
   */
  signAgreement: () => {
    return api.post('/dso/agreement/sign');
  },

  /**
   * Change the password of the currently authenticated DSO
   * @param {string} newPassword - The new password
   * @returns {Promise} API response with success message
   */
  changePassword: (newPassword) => {
    return api.patch('/dso/change-password', { new_password: newPassword });
  },

  /**
   * Get agents associated with the current DSO
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term to filter by name, email, or phone
   * @returns {Promise} API response with list of agents
   */
  getMyAgents: (params = {}) => {
    const { skip = 0, limit = 10, search } = params;
    let url = `/dso/agents?skip=${skip}&limit=${limit}`;

    // Add search parameter if provided
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return api.get(url);
  },
  
  /**
   * Toggle agent active status
   * @param {number} agentId - The agent ID to toggle
   * @param {boolean} active - New status value 
   * @returns {Promise} API response
   */
  toggleAgentStatus: (agentId, active) => {
    return api.put(`/dso/agents/${agentId}/status?active=${active}`);
  },
  
  /**
   * Get agent details by ID
   * @param {number} agentId - The agent ID
   * @returns {Promise} API response with agent details
   */
  getAgentById: (agentId) => {
    return api.get(`/dso/agents/${agentId}`);
  },
  
  /**
   * Update agent details
   * @param {number} agentId - The agent ID to update
   * @param {Object} agentData - Agent data to update
   * @returns {Promise} API response
   */
  updateAgent: (agentId, agentData) => {
    return api.put(`/dso/agents/${agentId}`, agentData);
  },
  
  /**
   * Upload document for an agent
   * @param {number} agentId - The agent ID
   * @param {FormData} formData - Form data with file and document type
   * @returns {Promise} API response
   */
  uploadAgentDocument: (agentId, formData) => {
    // Validate formData before sending to prevent object rendering issues
    for (const [key, value] of formData.entries()) {
      // Check if the value is a valid file or string
      if (value instanceof File) {
        console.log(`Uploading file: ${key}, name: ${value.name}, type: ${value.type}, size: ${value.size}`);
      } else if (typeof value === 'object' && value !== null) {
        // If it's an object but not a File, convert it to a string to prevent rendering issues
        formData.set(key, JSON.stringify(value));
      }
    }

    // Determine if we're using separate Aadhaar card files or single file with document_type
    const hasAadhaarFront = formData.has('adhaar_card_front_file');
    const hasAadhaarBack = formData.has('adhaar_card_back_file');
    
    // Support both old and new API formats
    return api.post(`/dso/agents/${agentId}/document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  /**
   * Get agent-specific commissions
   * @param {number} agentId - Agent ID
   * @returns {Promise} API response with agent details and commissions
   */
  getAgentCommissionsById: (agentId) => {
    return api.get(`/dso/agents/${agentId}/commissions`);
  },
  
  /**
   * Update agent-specific commissions
   * @param {number} agentId - Agent ID
   * @param {Array} commissionData - Array of commission objects
   * @returns {Promise} API response
   */
  updateAgentCommissionsById: (agentId, commissionData) => {
    return api.post(`/dso/agents/${agentId}/commissions`, commissionData);
  },
  
  /**
   * Create a new lead for a specific agent
   * @param {number} agentId - The agent ID
   * @param {Object} leadData - Lead data (name, email, mobile, etc.)
   * @returns {Promise} API response
   */
  createLeadForAgent: (agentId, leadData) => {
    return api.post(`/dso/agents/${agentId}/leads`, leadData);
  },
  
  /**
   * Bulk upload leads for a specific agent
   * @param {FormData} formData - Form data with CSV file and agent_id
   * @returns {Promise} API response
   */
  bulkUploadLeads: (formData) => {
    return api.post('/dso/leads/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // downloadLeadTemplate method has been removed as we now generate templates client-side
  
  /**
   * Get leads for the current DSO
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {number} params.agent_id - Optional filter by agent ID
   * @param {number} params.status - Optional filter by status
   * @param {string} params.search - Optional search term
   * @returns {Promise} API response with leads list
   */
  getLeads: (params = {}) => {
    const { skip = 0, limit = 10, agent_id, status, search } = params;
    let url = `/dso/leads?skip=${skip}&limit=${limit}`;
    
    if (agent_id !== undefined && agent_id !== null) {
      url += `&agent_id=${parseInt(agent_id)}`;
    }
    
    if (status !== undefined) {
      url += `&status=${status}`;
    }
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    console.log('Fetching leads with URL:', url);
    return api.get(url);
  },
  
  /**
   * Get backoffices assigned to the current DSO
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term
   * @returns {Promise} API response with list of backoffices
   */
  getBackoffices: (params = {}) => {
    const { skip = 0, limit = 10, search } = params;
    let url = `/dso/backoffices?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Create a new backoffice user and assign to the current DSO
   * @param {Object} backofficeData - Backoffice data (name, email, mobile, password)
   * @returns {Promise} API response
   */
  createBackoffice: (backofficeData) => {
    return api.post('/dso/backoffices', backofficeData);
  },
  
  /**
   * Toggle backoffice active status
   * @param {number} backofficeId - The backoffice user ID to toggle
   * @param {boolean} active - New status value
   * @returns {Promise} API response
   */
  toggleBackofficeStatus: (backofficeId, active) => {
    return api.put(`/dso/backoffices/${backofficeId}/status?active=${active}`);
  },
  
  /**
   * Reset password for a backoffice user
   * @param {number} backofficeId - The backoffice user ID
   * @param {string} newPassword - The new password to set
   * @returns {Promise} API response
   */
  resetBackofficePassword: (backofficeId, newPassword) => {
    return api.put(`/dso/backoffices/${backofficeId}/reset-password`, { password: newPassword });
  },
  
  /**
   * Get global commissions for the current DSO
   * @returns {Promise} API response with list of products and commissions
   */
  getCommissions: () => {
    return api.get('/dso/commissions');
  },
  
  /**
   * Update global agent commissions
   * @param {Array} commissionData - Array of commission objects 
   * @returns {Promise} API response
   */
  updateGlobalCommissions: (commissionData) => {
    return api.post('/dso/commissions', commissionData);
  },
  
  /**
   * Get agent-specific commissions
   * @param {number} agentId - Agent ID
   * @returns {Promise} API response with agent details and commissions
   */
  getAgentCommissions: (agentId) => {
    return api.get(`/dso/agents/${agentId}/commissions`);
  },
  
  /**
   * Update agent-specific commissions
   * @param {number} agentId - Agent ID
   * @param {Array} commissionData - Array of commission objects
   * @returns {Promise} API response
   */
  updateAgentCommissions: (agentId, commissionData) => {
    return api.post(`/dso/agents/${agentId}/commissions`, commissionData);
  },

  /**
   * Create a new invoice from selected cases in a payout
   * @param {Object} data - Invoice creation data 
   * @param {string|number} data.payout_id - The payout ID
   * @param {Array<number>} data.case_ids - Array of case IDs to include in the invoice
   * @returns {Promise} API response with created invoice details
   */
  createInvoice: (data) => {
    return api.post('/dso/invoices', data);
  },
  
  // --- Add Earnings Endpoints ---
  /**
   * Get summary earnings statistics for the current DSO
   * @returns {Promise} API response with summary data (e.g., { total_earnings, pending_payouts, average_commission, ... })
   */
  getEarningsSummary: () => {
    return api.get('/dso/earnings/summary');
  },

  /**
   * Get monthly earnings data for the current DSO (e.g., last 6 months)
   * @returns {Promise} API response with chart data (e.g., { labels: ['Jan', 'Feb', ...], data: [1000, 1200, ...] })
   */
  getMonthlyEarnings: () => {
    return api.get('/dso/earnings/monthly');
  },

  /**
   * Get earnings breakdown by product category for the current DSO
   * @returns {Promise} API response with category data (e.g., [{ category_name: 'Loans', total_amount: 5000 }, ...])
   */
  getEarningsByCategory: () => {
    return api.get('/dso/earnings/by-category');
  },
  
  /**
   * Get earnings breakdown by agent for the current DSO
   * @returns {Promise} API response with agents data (e.g., [{ id: 1, name: 'Agent Name', total_amount: 5000, ... }, ...])
   */
  getAgentEarnings: () => {
    return api.get('/dso/earnings/agents');
  },
  
  /**
   * Get invoices for the current DSO with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.invoice_number - Optional filter by invoice number
   * @param {string} params.approval_status - Optional filter by approval status
   * @param {string} params.payment_status - Optional filter by payment status
   * @returns {Promise} API response with invoice list
   */
  getInvoices: (params = {}) => {
    const { skip = 0, limit = 10, invoice_number, approval_status, payment_status } = params;
    let url = `/dso/invoices?skip=${skip}&limit=${limit}`;
    
    // Add filter params if provided
    if (invoice_number) {
      url += `&invoice_number=${encodeURIComponent(invoice_number)}`;
    }
    
    if (approval_status) {
      url += `&approval_status=${encodeURIComponent(approval_status)}`;
    }
    
    if (payment_status) {
      url += `&payment_status=${encodeURIComponent(payment_status)}`;
    }
    
    return api.get(url);
  },

  getAgentInvoices: (params = {}) => {
    const { skip = 0, limit = 10, invoice_number, approval_status, payment_status, agent_id } = params;
    let url = `/dso/agent-invoices?skip=${skip}&limit=${limit}`;
    
    // Add filter params if provided
    if (invoice_number) {
      url += `&invoice_number=${encodeURIComponent(invoice_number)}`;
    }
    
    if (approval_status) {
      url += `&approval_status=${encodeURIComponent(approval_status)}`;
    }
    
    if (payment_status) {
      url += `&payment_status=${encodeURIComponent(payment_status)}`;
    }

    if (agent_id) {
      url += `&agent_id=${encodeURIComponent(agent_id)}`;
    }
    
    return api.get(url);
  },

  /**
   * Get lead details by ID
   * @param {number} leadId - The lead ID to fetch
   * @returns {Promise} API response with lead data
   */
  getLeadById: (leadId) => {
    return api.get(`/dso/leads/${leadId}`);
  },

  /**
   * Get all cases associated with a specific lead
   * @param {number} leadId - The lead ID to fetch cases for
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with cases data
   */
  getLeadCases: (leadId, params = {}) => {
    const { skip = 0, limit = 10 } = params;
    return api.get(`/dso/leads/${leadId}/cases?skip=${skip}&limit=${limit}`);
  },

  /**
   * Update lead details
   * @param {number} leadId - The lead ID to update
   * @param {Object} leadData - Lead data to update
   * @returns {Promise} API response with updated lead data
   */
  updateLead: (leadId, leadData) => {
    return api.put(`/dso/leads/${leadId}`, leadData);
  },
  
  /**
   * Upload a document for a specific lead
   * @param {number} leadId - The lead ID
   * @param {FormData} formData - Form data with file and document type
   * @returns {Promise} API response with the document URL
   */
  uploadLeadDocument: (leadId, formData) => {
    return api.post(`/dso/leads/${leadId}/document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  /**
   * Get documents for a specific lead
   * @param {number} leadId - The lead ID
   * @returns {Promise} API response with lead documents (adhaar_card_file, pan_card_file, voter_id_card_file, bank_statements_file, bank_soa_file, form_16_file, office_id_card_file)
   */
  getLeadDocuments: (leadId) => {
    return api.get(`/dso/leads/${leadId}/documents`);
  },

  /**
   * Get business loan documents for a specific lead
   * @param {number} leadId - The lead ID
   * @returns {Promise} API response with business loan documents
   */
  getLoanDocuments: (leadId) => {
    return api.get(`/loan-documents/${leadId}`);
  },
  
  /**
   * Upload a business loan document for a specific lead
   * @param {number} leadId - The lead ID
   * @param {FormData} formData - Form data with file, document_type and business_type
   * @returns {Promise} API response with updated business loan document
   */
  uploadLoanDocument: (leadId, formData) => {
    return api.post(`/loan-documents/${leadId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  /**
   * Get required documents for a specific business type
   * @param {string} businessType - The business type (Partnership Firm, Pvt Ltd Company, Proprietorship)
   * @returns {Promise} API response with required document types
   */
  getRequiredLoanDocuments: (businessType) => {
    return api.get(`/loan-documents/required-documents/${encodeURIComponent(businessType)}`);
  },
  
  /**
   * Check document completeness for a specific lead
   * @param {number} leadId - The lead ID
   * @returns {Promise} API response with document completion status
   */
  checkDocumentCompleteness: (leadId) => {
    return api.get(`/loan-documents/check-completeness/${leadId}`);
  },
  
  /**
   * Push a lead and its associated cases to backoffice
   * @param {number} leadId - The lead ID to push to backoffice
   * @param {Object} data - Object containing case_ids array of case IDs to push
   * @returns {Promise} API response
   */
  pushToBackoffice: (leadId, data) => {
    return api.post(`/dso/leads/${leadId}/push-to-backoffice`, data);
  },
  
  /**
   * Update the status of a specific case
   * @param {number} caseId - The case ID to update
   * @param {Object} data - Object containing case_status with new status value
   * @returns {Promise} API response
   */
  updateCaseStatus: (caseId, data) => {
    return api.put(`/dso/cases/${caseId}/status`, data);
  },
  
  /**
   * Get invoice details by ID
   * @param {string|number} invoiceId - The invoice ID
   * @returns {Promise} API response with detailed invoice information
   */
  getInvoiceDetails: (invoiceId) => {
    return api.get(`/dso/invoices/${invoiceId}`);
  },
  
  // --- Add this function within the dsoApi object ---
  /**
   * Update the status (approval and payment) of a specific invoice
   * @param {string|number} invoiceId - The ID of the invoice
   * @param {Object} statusData - Object containing status updates
   * @param {number} statusData.approval_status - New approval status code
   * @param {number} statusData.payment_status - New payment status code
   * @returns {Promise} API response with the updated invoice details
   */
  updateInvoiceStatus: (invoiceId, statusData) => {
    // Makes PUT request to update both approval and payment status
    // Ensure this endpoint matches the backend route definition
    return api.put(`/dso/invoices/${invoiceId}/status`, statusData);
  },
  // --- End of added function ---
  
  /**
   * Get payouts for the current DSO with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.status - Optional filter by status
   * @returns {Promise} API response with payout list
   */
  getPayouts: (params = {}) => {
    const { skip = 0, limit = 10, status } = params;
    let url = `/dso/payouts?skip=${skip}&limit=${limit}`;
    
    // Add filter params if provided
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    return api.get(url).then(response => {
      // Ensure case_count is properly provided for each payout
      if (response && response.data && response.data.items) {
        response.data.items = response.data.items.map(payout => ({
          ...payout,
          case_count: payout.case_count || 0 // Ensure case_count is never null/undefined
        }));
      }
      return response;
    });
  },
  
  /**
   * Get payout details by ID
   * @param {string|number} payoutId - The payout ID
   * @returns {Promise} API response with detailed payout information
   */
  getPayoutDetails: (payoutId) => {
    return api.get(`/dso/payouts/${payoutId}`).then(async response => {
      if (response && response.data) {
        // Ensure case_count is never null or undefined
        if (response.data.case_count === null || response.data.case_count === undefined) {
          try {
            // If case_count is missing, fetch the cases and use that length
            const casesResponse = await api.get(`/dso/payouts/${payoutId}/cases?limit=1000`);
            if (casesResponse?.data?.items) {
              response.data.case_count = casesResponse.data.items.length;
            } else {
              response.data.case_count = 0;
            }
          } catch (error) {
            console.error("Error fetching cases for count:", error);
            response.data.case_count = 0;
          }
        }
      }
      return response;
    });
  },
  
  /**
   * Get cases associated with a specific payout
   * @param {string|number} payoutId - The payout ID
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with cases list
   */
  getPayoutCases: (payoutId, params = {}) => {
    const { skip = 0, limit = 10 } = params;
    return api.get(`/dso/payouts/${payoutId}/cases?skip=${skip}&limit=${limit}`).then(response => {
      if (response && response.data) {
        // Make sure we always have a total field even if it's missing
        if (!response.data.total && response.data.items) {
          response.data.total = response.data.items.length;
        }
      }
      return response;
    });
  },

  /**
   * Approve an agent payout request.
   * @param {number|string} payoutId - The ID of the payout to approve.
   * @returns {Promise} API response with updated payout details.
   */
  approveAgentPayout: (payoutId) => {
    // Ensure this endpoint matches the backend route definition
    return api.put(`/dso/agent-payouts/${payoutId}/approve`);
  },

  /**
   * Request a new payout for a specific agent managed by the current DSO
   * @param {Object} payoutData - Payout data
   * @param {number} payoutData.agent_id - The agent user ID
   * @param {Array<number>} payoutData.case_ids - Array of case IDs to include in payout
   * @returns {Promise} API response with created payout details
   */
  createAgentPayoutRequest: (payoutData) => {
    // Ensure this endpoint matches the backend route for DSO creating an agent payout request
    // Example: POST /dso/agents/payouts/request
    return api.post('/dso/agents/payouts/request', payoutData);
  },

  /**
   * Get payouts requested by a specific agent managed by the current DSO
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.status - Optional filter by status (e.g., "created", "approved")
   * @returns {Promise} API response with payout list { items: [], total: number }
   */
  getAgentPayouts: (params = {}) => {
    const { skip = 0, limit = 10, status } = params;
    // Ensure this endpoint matches the one defined in backend/app/routers/dso.py
    let url = `/dso/agents/payouts?skip=${skip}&limit=${limit}`;

    // Add status filter if provided
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }

    return api.get(url);
  },
  
  /**
   * Get invoices associated with a specific payout
   * @param {string|number} payoutId - The payout ID
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with payout invoices list
   */
  getPayoutInvoices: (payoutId, params = {}) => {
    const { skip = 0, limit = 10 } = params;
    return api.get(`/dso/payouts/${payoutId}/invoices?skip=${skip}&limit=${limit}`);
  },

  /**
   * Request a new payout
   * @param {Object} payoutData - Object containing case_ids to include in the payout
   * @returns {Promise} API response
   */
  requestPayout: (payoutData) => {
    return api.post('/dso/payouts/request', payoutData);
  },

  /**
   * Get eligible cases for payout
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with list of eligible cases
   */
  getEligibleCasesForPayout: (params = {}) => {
    const { skip = 0, limit = 100 } = params;
    // Use the leads endpoint with status filter for approved cases (status=2) that are eligible for payouts
    return api.get(`/dso/leads?skip=${skip}&limit=${limit}&status=13`)
      .then(response => {
        // Transform response to ensure items is an array
        if (response && response.data) {
          // If data.items exist, use it; otherwise wrap data in an items property if it's an array
          if (!response.data.items && Array.isArray(response.data)) {
            return { data: { items: response.data, total: response.data.length } };
          }
        }
        return response;
      });
  },

  /**
   * Get lead status types.
   * @returns {Promise} API response with a list of lead status types (e.g., [{ id: 1, name: 'New' }, ...])
   */
  getLeadStatusTypes: () => {
    // Ensure this endpoint matches your backend route in dso.py
    return api.get(`/dso/status-types/lead`);
  },
  // --- End Earnings Endpoints ---

  // --- Add Agent Endpoint ---
  createAgent: (agentData) => {
    // agentData should match the AgentCreate schema { name, email, mobile, password }
    return api.post('/dso/agents', agentData);
  },
  // --- End Add Agent Endpoint ---
  
  /**
   * Reset an agent's password
   * @param {number} agentId - The agent's user ID
   * @returns {Promise} API response
   */
  resetAgentPassword: (agentId) => {
    return api.put(`/dso/agents/${agentId}/reset-password`);
  },
  
  /**
   * Get products assigned to a specific agent
   * @param {number} agentId - The agent's ID
   * @returns {Promise} API response with assigned products
   */
  getAgentProducts: (agentId) => {
    return api.get(`/dso/agents/${agentId}/products`);
  },
  
  /**
   * Update product assignments for a specific agent
   * @param {number} agentId - The agent's ID
   * @param {Object} productData - Object containing product assignments { product_ids: number[] }
   * @returns {Promise} API response
   */
  updateAgentProducts: (agentId, productData) => {
    return api.post(`/dso/agents/${agentId}/products`, productData);
  },

  /**
   * Upload a document for the currently authenticated DSO
   * @param {FormData} formData - FormData containing the file and document_type
   * @returns {Promise} API response with the uploaded document URL
   */
  uploadDsoDocument: (formData) => {
    // Extract document_type from formData
    const documentType = formData.get('document_type');
    const file = formData.get('file');
    
    // Keep the document type as is - to match the backend's expected field mapping
    // Backend expects: adhaar_front, adhaar_back, pan_card, company_pan_card, company_certificate
    let backendDocumentType = documentType;
    
    // Just for compatibility with any existing code
    if (documentType === 'company_pan') {
      backendDocumentType = 'company_pan_card';
    }
    
    // Create a new FormData with the correct fields
    const fileFormData = new FormData();
    fileFormData.append('file', file);
    fileFormData.append('document_type', backendDocumentType);
    
    console.log(`Uploading document: ${documentType} as type: ${backendDocumentType}`);
    console.log(`File details: name=${file.name}, type=${file.type}, size=${file.size}`);
    
    // Send the request with the document_type as a form field, not query param
    return api.post('/dso/upload-document', fileFormData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
}; // End of dsoApi object

// Define Agent API functions
export const agentApi = {
  /**
   * Reset Agent's password (Admin only)
   * @param {number} agentId - The Agent's User ID
   * @param {string} newPassword - The new password to set
   * @returns {Promise} API response
   */
  resetAgentPassword: (agentId, newPassword) => {
    // Ensure the endpoint matches the backend router definition
    return api.put(`/admin/agent/${agentId}/reset-password`, { password: newPassword });
  },
  
  /**
   * Get the profile of the currently authenticated Agent
   * @returns {Promise} API response with the Agent profile details
   */
  getAgentProfile: () => {
    return api.get('/agent/profile');
  },

  /**
   * Update the profile of the currently authenticated Agent
   * @param {Object} profileData - The profile data to update
   * @returns {Promise} API response with the updated Agent profile
   */
  updateAgentProfile: (profileData) => {
    return api.put('/agent/profile', profileData);
  },

  /**
   * Change the password of the currently authenticated Agent
   * @param {string} newPassword - The new password
   * @returns {Promise} API response with success message
   */
  changePassword: (newPassword) => {
    return api.patch('/agent/change-password', { new_password: newPassword });
  },

  /**
   * Upload a document for the currently authenticated Agent
   * @param {FormData} formData - FormData with file and document_type
   * @returns {Promise} API response with the uploaded file URL and updated profile
   */
  uploadDocument: (formData) => {
    return api.post('/agent/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  /**
   * Update bank details for the currently authenticated Agent
   * @param {Object} bankDetails - Bank details data
   * @returns {Promise} API response with updated bank details
   */
  updateAgentBankDetails: (bankDetails) => {
    return api.put('/agent/bank-details', bankDetails);
  },

  /**
   * Get leads/cases for the current agent with optional filtering
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {number} params.status - Optional status filter
   * @param {number} params.product_id - Optional product filter
   * @param {string} params.search - Optional search term
   * @param {string} params.agent_id - Optional explicit agent ID for testing
   * @returns {Promise} API response with leads data
   */
  getLeads: (params = {}) => {
    const { skip = 0, limit = 10, status, product_id, search, agent_id } = params;
    
    let url = `/agent/leads?skip=${skip}&limit=${limit}`;
    
    if (status !== undefined && status !== null) {
      url += `&status=${status}`;
    }
    
    if (product_id !== undefined && product_id !== null) {
      url += `&product_id=${product_id}`;
    }
    

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (agent_id) {
      url += `&agent_id=${encodeURIComponent(agent_id)}`;
    }
    
    return api.get(url);
  },

  getApprovedPayoutCases : (params = {}) => {
    const { skip = 0, limit = 10, status, product_id, search } = params;
    
    let url = `/agent/invoice-cases?skip=${skip}&limit=${limit}`;
    
    if (status !== undefined && status !== null) {
      url += `&status=${status}`;
    }
    
    if (product_id !== undefined && product_id !== null) {
      url += `&product_id=${product_id}`;
    }
    

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get earnings statistics for the current Agent
   * @returns {Promise} API response with statistics
   */
  getEarningsStats: () => {
    return api.get('/agent/earnings/stats');
  },

  /**
   * Get monthly earnings data for the current Agent
   * @returns {Promise} API response with chart data
   */
  getMonthlyEarnings: () => {
    return api.get('/agent/earnings/monthly');
  },

  /**
   * Get earnings breakdown by product for the current Agent
   * @returns {Promise} API response with product data
   */
  getEarningsByProduct: () => {
    return api.get('/agent/earnings/by-product');
  },

  /**
   * Get invoices for the current Agent with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.approval_status - Optional filter by approval status
   * @param {string} params.payment_status - Optional filter by payment status
   * @returns {Promise} API response with invoice list
   */
  getInvoices: (params = {}) => {
    const { skip = 0, limit = 10, approval_status, payment_status } = params;
    let url = `/agent/invoices?skip=${skip}&limit=${limit}`;
    
    if (approval_status) {
      url += `&approval_status=${encodeURIComponent(approval_status)}`;
    }
    
    if (payment_status) {
      url += `&payment_status=${encodeURIComponent(payment_status)}`;
    }
    
    return api.get(url);
  },

  /**
   * Get invoice details by ID
   * @param {string|number} invoiceId - The invoice ID
   * @returns {Promise} API response with detailed invoice information
   */
  getInvoiceDetails: (invoiceId) => {
    return api.get(`/agent/invoices/${invoiceId}`);
  },

  /**
   * Create a new invoice from selected cases in a payout
   * @param {Object} data - Invoice creation data 
   * @param {string|number} data.payout_id - The payout ID
   * @param {Array<number>} data.case_ids - Array of case IDs to include in the invoice
   * @returns {Promise} API response with created invoice details
   */
  createInvoice: (data) => {
    return api.post('/agent/invoices', data);
  },

  /**
   * Get payouts for the current Agent with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.status - Optional filter by status
   * @returns {Promise} API response with payout list
   */
  getPayouts: (params = {}) => {
    const { skip = 0, limit = 10, status } = params;
    let url = `/agent/payouts?skip=${skip}&limit=${limit}`;
    
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    return api.get(url);
  },

  // getPayoutDetails removed
  // getPayoutCases removed
  // getPayoutInvoices removed

  /**
   * Get assigned products for the current Agent
   * @returns {Promise} API response with list of products
   */
  getAssignedProducts: () => {
    return api.get('/agent/products');
  },
    
   /* Get summary data for the current agent's leads
   * @returns {Promise} API response with summary data
   */
  getLeadsSummary: () => {
    return api.get('/agent/leads/summary');
  },
  
  /**
   * Add a new lead as an agent
   * @param {Object} leadData - Lead data (name, email, mobile, case_type, product_id)
   * @returns {Promise} API response
   */
  addLead: (leadData) => {
    return api.post('/agent/leads', leadData);
  },
  
  /**
   * Bulk upload leads for an agent
   * @param {FormData} formData - Form data with CSV file
   * @returns {Promise} API response
   */
  bulkUploadLeads: (formData) => {
    return api.post('/agent/leads/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  /**
   * Get CIBIL data for a specific CIBIL ID
   * @param {String} cibilId - The CIBIL ID to fetch data for
   * @returns {Promise} API response with CIBIL data
   */
  getCibilData: (cibilId) => {
    return api.post('/agent/cibil-data', { cibil_id: cibilId });
  },
  
  /**
   * Request a CIBIL check for a lead
   * @param {Number} leadId - The lead ID to check CIBIL for
   * @returns {Promise} API response
   */
  requestCibilCheck: (leadId) => {
    return api.post(`/agent/leads/${leadId}/request-cibil-check`);
  },
  
  /**
   * Get lead details by ID
   * @param {number} leadId - The lead ID
   * @returns {Promise} API response with lead details
   */
  getLeadDetails: (leadId) => {
    return api.get(`/agent/lead/${leadId}`);
  },
  
  /**
   * Update lead personal details
   * @param {number} leadId - The lead ID
   * @param {Object} personalDetails - Personal details to update
   * @returns {Promise} API response
   */
  updateLeadPersonalDetails: (leadId, personalDetails) => {
    return api.put(`/agent/lead/${leadId}/personal`, personalDetails);
  },
  
  /**
   * Update lead professional details
   * @param {number} leadId - The lead ID
   * @param {Object} professionalDetails - Professional details to update
   * @returns {Promise} API response
   */
  updateLeadProfessionalDetails: (leadId, professionalDetails) => {
    return api.put(`/agent/lead/${leadId}/professional`, professionalDetails);
  },
  
  /**
   * Upload document for a lead
   * @param {number} leadId - The lead ID
   * @param {FormData} formData - Form data with file and document type
   * @returns {Promise} API response
   */
  uploadLeadDocument: (leadId, formData) => {
    return api.post(`/agent/lead/${leadId}/document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  /**
   * Get lead details by ID
   * @param {number} leadId - The lead ID to fetch
   * @returns {Promise} API response with lead data
   */
  getLeadById: (leadId) => {
    return api.get(`/agent/leads/${leadId}`);
  },

  /**
   * Get all cases associated with a specific lead
   * @param {number} leadId - The lead ID to fetch cases for
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @returns {Promise} API response with cases data
   */
  getLeadCases: (leadId, params = {}) => {
    const { skip = 0, limit = 10 } = params;
    return api.get(`/agent/leads/${leadId}/cases?skip=${skip}&limit=${limit}`);
  },

  /**
   * Update lead details
   * @param {number} leadId - The lead ID to update
   * @param {Object} leadData - Lead data to update
   * @returns {Promise} API response with updated lead data
   */
  updateLead: (leadId, leadData) => {
    return api.put(`/agent/leads/${leadId}`, leadData);
  },
  
  /**
   * Upload a document for a specific lead
   * @param {number} leadId - The lead ID
   * @param {FormData} formData - Form data with file and document type
   * @returns {Promise} API response with the document URL
   */
  uploadLeadDocument: (leadId, formData) => {
    return api.post(`/agent/leads/${leadId}/document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  /**
   * Get documents for a specific lead
   * @param {number} leadId - The lead ID
   * @returns {Promise} API response with lead documents (adhaar_card_file, pan_card_file, voter_id_card_file, bank_statements_file)
   */
  getLeadDocuments: (leadId) => {
    return api.get(`/agent/leads/${leadId}/documents`);
  },
  
  /**
   * Push a lead and its associated cases to backoffice
   * @param {number} leadId - The lead ID to push to backoffice
   * @param {Object} data - Object containing case_ids array of case IDs to push
   * @returns {Promise} API response
   */
  pushToBackoffice: (leadId, data) => {
    return api.post(`/agent/leads/${leadId}/push-to-backoffice`, data);
  },
  
  /**
   * Update the status of a specific case
   * @param {number} caseId - The case ID to update
   * @param {Object} data - Object containing case_status with new status value
   * @returns {Promise} API response
   */
  updateCaseStatus: (caseId, data) => {
    return api.put(`/agent/cases/${caseId}/status`, data);
  },
  
  /**
   * Get lead status types.
   * @returns {Promise} API response with a list of lead status types (e.g., [{ id: 1, name: 'New' }, ...])
   */
  getLeadStatusTypes: () => {
    return api.get(`/agent/status-types/lead`);
  }
}; // End of agentApi object

// Define Notification API functions
// Define backoffice API functions
export const backofficeApi = {
  /**
   * Get the profile of the currently authenticated backoffice user
   * @returns {Promise} API response with the backoffice profile details
   */
  getProfile: () => {
    return api.get('/backoffice/me');
  },

  /**
   * Update the profile of the currently authenticated backoffice user
   * @param {Object} profileData - The profile data to update
   * @returns {Promise} API response with the updated backoffice profile
   */
  updateProfile: (profileData) => {
    return api.put('/backoffice/me', profileData);
  },

  /**
   * Reset password for the currently authenticated backoffice user
   * @param {string} newPassword - The new password
   * @returns {Promise} API response
   */
  resetPassword: (newPassword) => {
    return api.put('/backoffice/me/reset-password', { password: newPassword });
  },

  /**
   * Get applications (cases) for the backoffice team
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term for customer name or mobile
   * @param {number} params.status - Optional status code to filter by
   * @param {number} params.product_id - Optional product ID to filter by
   * @param {number} params.searched_dso_id - Optional searched dso id to filter
   * @returns {Promise} API response with applications list
   */
  getApplications: (params = {}) => {
    const { skip = 0, limit = 10, search, status, product_id,searched_dso_id } = params;
    let url = `/backoffice/applications?skip=${skip}&limit=${limit}`;
    
    // Add search parameter if provided
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    // Add status filter if provided
    if (status !== undefined && status !== null) {
      url += `&status=${status}`;
    }
    
    // Add product filter if provided
    if (product_id !== undefined && product_id !== null) {
      url += `&product_id=${product_id}`;
    }
    if(searched_dso_id){
      url +=`&searched_dso_id=${searched_dso_id}`
    }
    
    return api.get(url);
  },

  /**
   * Update the status of a specific case
   * @param {number} caseId - The ID of the case to update
   * @param {number} status - The new status code to set
   * @returns {Promise} API response with updated case data
   */
  updateCaseStatus: (caseId, status) => {
    return api.put(`/backoffice/cases/${caseId}/status`, {
      case_status: status
    });
  },
  
  /**
   * Batch update multiple cases with the same status
   * @param {Array<number>} caseIds - Array of case IDs to update
   * @param {number} status - New status code to apply to all cases
   * @returns {Promise} API response with batch update results
   */
  batchUpdateCaseStatus: (caseIds, status) => {
    return api.post('/backoffice/cases/batch-update-status', {
      case_ids: caseIds,
      case_status: status
    });
  },

  /**
   * Get notes for a specific case
   * @param {number} caseId - The ID of the case to get notes for
   * @returns {Promise} API response with case notes
   */
  getCaseNotes: (caseId) => {
    return api.get(`/backoffice/cases/${caseId}/notes`);
  },

  /**
   * Update notes for a specific case
   * @param {number} caseId - The ID of the case to update notes for
   * @param {string} notes - The notes text to save
   * @returns {Promise} API response with updated case notes
   */
  updateCaseNotes: (caseId, notes) => {
    return api.put(`/backoffice/cases/${caseId}/notes`, {
      notes: notes
    });
  },

  /**
   * Get detailed information for a specific case, including documents
   * @param {number} caseId - The ID of the case to get details for
   * @returns {Promise} API response with case details, lead details and document URLs
   */
  getCaseDetails: (caseId) => {
    return api.get(`/backoffice/cases/${caseId}/details`);
  },
  
  /**
   * Get invoices for the backoffice team to review
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term for invoice number or DSO/agent name
   * @param {number} params.status - Optional status code to filter by
   * @returns {Promise} API response with invoice list
   */
  getInvoices: (params = {}) => {
    const { skip = 0, limit = 10, search, status } = params;
    let url = `/backoffice/invoices?skip=${skip}&limit=${limit}`;
    
    // Add search parameter if provided
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    // Add status filter if provided
    if (status !== undefined && status !== null) {
      url += `&status=${status}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get detailed information about a specific invoice
   * @param {number} invoiceId - The ID of the invoice to retrieve
   * @returns {Promise} API response with detailed invoice information
   */
  getInvoiceDetails: (invoiceId) => {
    return api.get(`/backoffice/invoices/${invoiceId}`);
  },
  
  /**
   * Update the status of an invoice
   * @param {number} invoiceId - The ID of the invoice to update
   * @param {Object} statusData - Object with status and optional notes
   * @param {number} statusData.status - New status value (1=Processing, 2=Disbursed, 3=Rejected)
   * @param {string} statusData.notes - Optional notes for this status change
   * @returns {Promise} API response with updated invoice
   */
  updateInvoiceStatus: (invoiceId, statusData) => {
    return api.put(`/backoffice/invoices/${invoiceId}/status`, statusData);
  },
  
  /**
   * Get cases associated with a specific invoice
   * @param {number} invoiceId - The ID of the invoice
   * @returns {Promise} API response with case items
   */
  getInvoiceCases: (invoiceId) => {
    return api.get(`/backoffice/invoices/${invoiceId}/cases`);
  }
};

export const notificationApi = {
  /**
   * Get notifications for the current user (RM)
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max notifications (defaults to 10)
   * @param {number} params.skip - Offset
   * @returns {Promise} API response with notification list
   */
  getNotifications: (params = {}) => {
    const { limit = 10, skip = 0 } = params;
    // Ensure the prefix matches how you included the router in FastAPI (e.g., /api)
    return api.get(`/notifications?skip=${skip}&limit=${limit}`);
  },

  /**
   * Get the count of unread notifications for the current user (RM)
   * @returns {Promise} API response with { unread_count: number }
   */
  getUnreadCount: () => {
    return api.get('/notifications/count');
  },

  /**
   * Mark a specific notification as read
   * @param {number | string} notificationId - The ID of the notification
   * @returns {Promise} API response with the updated notification
   */
  markAsRead: (notificationId) => {
    return api.put(`/notifications/${notificationId}/read`);
  },

  /**
   * Mark all unread notifications as read
   * @returns {Promise} API response (status code 204 on success)
   */
  markAllAsRead: () => {
    return api.put('/notifications/read-all');
  }
}; // End of notificationApi object

// Define WFBackoffice API functions
export const wfbackofficeApi = {
  /**
   * Get dashboard statistics for Wishfin Backoffice
   * @returns {Promise} API response with dashboard statistics
   */
  getDashboardStats: () => {
    return api.get('/wfbackoffice/dashboard-stats');
  },

  /**
   * Get invoices with optional filtering and sorting
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term for invoice number or DSO/agent name
   * @param {string} params.status - Optional filter by approval status
   * @param {string} params.sort_by - Optional field to sort by
   * @param {boolean} params.sort_desc - Optional sort direction (true for descending)
   * @param {boolean} params.dso_only - Optional filter to get only DSO invoices
   * @param {number} params.dso_id - Optional filter by specific DSO ID
   * @returns {Promise} API response with invoice list
   */
  getInvoices: (params = {}) => {
    const { skip = 0, limit = 10, search, status, sort_by, sort_desc, dso_only, dso_id } = params;
    let url = `/wfbackoffice/invoices?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status !== undefined && status !== null) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    if (sort_by) {
      url += `&sort_by=${encodeURIComponent(sort_by)}`;
    }
    
    if (sort_desc !== undefined) {
      url += `&sort_desc=${sort_desc}`;
    }
    
    // Filter for DSO invoices only
    if (dso_only === true) {
      url += '&dso_only=true';
    }
    
    // Filter for a specific DSO
    if (dso_id !== undefined && dso_id !== null) {
      url += `&dso_id=${dso_id}`;
    }
    
    return api.get(url);
  },

  /**
   * Get detailed information about a specific invoice
   * @param {number} invoiceId - The ID of the invoice to retrieve
   * @returns {Promise} API response with detailed invoice information
   */
  getInvoiceDetails: (invoiceId) => {
    return api.get(`/wfbackoffice/invoices/${invoiceId}`);
  },

  /**
   * Update the status of an invoice
   * @param {number} invoiceId - The ID of the invoice to update
   * @param {Object} statusData - Object with status and optional notes
   * @param {string} statusData.status - New status value (e.g., "Approved", "Rejected", "Processing")
   * @param {string} statusData.notes - Optional notes for this status change
   * @returns {Promise} API response with updated invoice
   */
  updateInvoiceStatus: (invoiceId, statusData) => {
    const queryParams = new URLSearchParams();
    queryParams.append('status', statusData.status);
    if (statusData.notes) {
      queryParams.append('notes', statusData.notes);
    }
    return api.put(`/wfbackoffice/invoices/${invoiceId}/status?${queryParams.toString()}`);
  },

  /**
   * Get dashboard statistics for Wishfin Backoffice
   * @returns {Promise} API response with dashboard statistics
   */
  getDashboardStats: () => {
    return api.get('/wfbackoffice/dashboard-stats');
  },

  /**
   * Get a list of DSO partners with optional filtering
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {string} params.search - Optional search term for DSO name or ID
   * @param {string} params.status - Optional filter by status (active/inactive)
   * @returns {Promise} API response with DSO partner list
   */
  getDsoPartners: (params = {}) => {
    const { skip = 0, limit = 10, search, status } = params;
    let url = `/wfbackoffice/dso-partners?skip=${skip}&limit=${limit}`;
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get detailed information about a specific DSO partner
   * @param {number} dsoId - The ID of the DSO partner to retrieve details for
   * @returns {Promise} API response with detailed DSO partner information including lead counts
   */
  getDsoPartnerDetails: (dsoId) => {
    return api.get(`/wfbackoffice/dso-partners/${dsoId}`);
  },

  /**
   * Get a list of agents with optional filtering
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum number of records to fetch
   * @param {number} params.dso_id - Optional filter by DSO ID
   * @param {string} params.search - Optional search term for agent name or ID
   * @param {string} params.status - Optional filter by status (active/inactive)
   * @returns {Promise} API response with agent list
   */
  getAgents: (params = {}) => {
    const { skip = 0, limit = 10, dso_id, search, status } = params;
    let url = `/wfbackoffice/agents?skip=${skip}&limit=${limit}`;
    
    if (dso_id !== undefined && dso_id !== null) {
      url += `&dso_id=${dso_id}`;
    }
    
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    
    return api.get(url);
  },
  
  /**
   * Get detailed information about a specific agent
   * @param {number} agentId - The ID of the agent to retrieve details for
   * @returns {Promise} API response with detailed agent information including lead counts
   */
  getAgentDetails: (agentId) => {
    return api.get(`/wfbackoffice/agents/${agentId}`);
  },

  /**
   * Get cases associated with a specific WF Backoffice invoice
   * @param {number} invoiceId - The ID of the invoice
   * @returns {Promise} API response with list of cases { items: [], total: number }
   */
  getInvoiceCases: (invoiceId) => {
    // Ensure the endpoint matches your backend route definition
    return api.get(`/wfbackoffice/invoices/${invoiceId}/cases`);
  },
}; // End of wfbackofficeApi object


export const commonApi = {
  getStatusList: () => {
    return api.get('/common/status-types');
  },

  getDsoList: () => {
    return api.get('/common/dso-list');
  }
};
export default api;
